// src/components/analysis/detail/MarketAnalysisDetail.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  Chip,
  Avatar
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import BarChartIcon from '@mui/icons-material/BarChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InfoIcon from '@mui/icons-material/Info';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShowChartIcon from '@mui/icons-material/ShowChart';

// Helper function to format keys for display
const formatEvaluationKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Custom list item component for better formatting
const EnhancedListItem = ({ children }) => (
  <ListItem 
    sx={{ 
      padding: 0,
      paddingY: 0.5,
      marginLeft: 0
    }}
  >
    <ListItemIcon sx={{ minWidth: 28 }}>
      <CheckCircleOutlineIcon color="primary" fontSize="small" />
    </ListItemIcon>
    <ListItemText 
      primary={
        <Typography variant="body2">
          {children}
        </Typography>
      } 
    />
  </ListItem>
);

// Section header component
const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
      {icon}
    </Avatar>
    <Typography variant="h6" color="primary.main" fontWeight="bold">
      {title}
    </Typography>
  </Box>
);

// Competitor card component
const CompetitorCard = ({ competitor }) => (
  <Box sx={{ mb: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
      {competitor.name}
    </Typography>
    {Array.isArray(competitor.key_characteristics) && competitor.key_characteristics.length > 0 && (
      <List dense disablePadding>
        {competitor.key_characteristics.map((characteristic, i) => (
          <EnhancedListItem key={i}>
            {typeof characteristic === 'string' ? characteristic : JSON.stringify(characteristic)}
          </EnhancedListItem>
        ))}
      </List>
    )}
  </Box>
);

const MarketAnalysisDetail = ({ data }) => {
  const market = data.market || {};
  const competition = data.competition || {};
  const evaluation = data.evaluation || {};

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader 
              icon={<TrendingUpIcon fontSize="small" />}
              title="Market Analysis"
            />
            
            {market.size && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Market Size:
                </Typography>
                <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  {market.size}
                </Typography>
              </Box>
            )}
            
            {market.growth && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShowChartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Market Growth:
                </Typography>
                <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  {market.growth}
                </Typography>
              </Box>
            )}
            
            {Array.isArray(market.segments) && market.segments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CompareArrowsIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Market Segments:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {market.segments.map((segment, idx) => (
                    <Chip 
                      key={idx} 
                      label={segment} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {Array.isArray(market.trends) && market.trends.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Market Trends:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <List dense disablePadding>
                    {market.trends.map((trend, index) => (
                      <EnhancedListItem key={index}>
                        {trend}
                      </EnhancedListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
            
            {Array.isArray(market.regulatory_factors) && market.regulatory_factors.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Regulatory Factors:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <List dense disablePadding>
                    {market.regulatory_factors.map((factor, index) => (
                      <EnhancedListItem key={index}>
                        {factor}
                      </EnhancedListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader 
              icon={<BusinessIcon fontSize="small" />}
              title="Competitive Analysis"
            />
            
            {Array.isArray(competition.direct_competitors) && competition.direct_competitors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CompareArrowsIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Direct Competitors:
                </Typography>
                {competition.direct_competitors.map((competitor, index) => (
                  <CompetitorCard key={index} competitor={competitor} />
                ))}
              </Box>
            )}
            
            {Array.isArray(competition.indirect_competitors) && competition.indirect_competitors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Indirect Competitors:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <List dense disablePadding>
                    {competition.indirect_competitors.map((item, index) => (
                      <EnhancedListItem key={index}>
                        {typeof item === 'string' ? item : (item && item.name) ? item.name : 'Invalid competitor format'}
                      </EnhancedListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
            
            {Array.isArray(competition.competitive_advantages) && competition.competitive_advantages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocalOfferIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Competitive Advantages:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {competition.competitive_advantages.map((advantage, index) => (
                    <Chip
                      key={index}
                      label={advantage}
                      color="primary"
                      variant="outlined"
                      sx={{ justifyContent: 'flex-start', height: 'auto', padding: '8px 0', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {competition.positioning && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Positioning:
                </Typography>
                <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  {competition.positioning}
                </Typography>
              </Box>
            )}
            
            {Array.isArray(competition.comparison_points) && competition.comparison_points.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CompareArrowsIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Comparison Points:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <List dense disablePadding>
                    {competition.comparison_points.map((point, index) => (
                      <EnhancedListItem key={index}>
                        {point}
                      </EnhancedListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <StarIcon sx={{ mr: 1 }} />
        Market Evaluation
      </Typography>
      
      <Grid container spacing={3}>
        {Object.entries(evaluation)
          .filter(([key]) => key !== 'overall_market_position')
          .map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderWidth: 1, 
                  borderColor: 'primary.light',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 1
                  }
                }}
              >
                <CardHeader 
                  title={formatEvaluationKey(key)} 
                  titleTypographyProps={{ fontWeight: 'bold', variant: 'subtitle1' }}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                  }}
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Rating 
                        value={parseFloat(value.score)} 
                        precision={0.5} 
                        readOnly 
                        emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 1, 
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {value.score}/5
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2">
                    {value.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
      
      {evaluation.overall_market_position && (
        <Box sx={{ mt: 3 }}>
          <Card 
            elevation={2}
            sx={{ 
              borderRadius: 1,
              borderLeft: 4, 
              borderColor: 'primary.main'
            }}
          >
            <CardHeader 
              title="Overall Market Position Assessment" 
              titleTypographyProps={{ fontWeight: 'bold', variant: 'h6', color: 'primary.main' }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StarIcon />
                </Avatar>
              }
              subheader={
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Rating 
                    value={parseFloat(evaluation.overall_market_position.score)} 
                    precision={0.5} 
                    readOnly 
                    sx={{ color: 'primary.main' }}
                    emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                  />
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    {evaluation.overall_market_position.score}/5
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              <Typography variant="body1">
                {evaluation.overall_market_position.summary}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default MarketAnalysisDetail;
